import React from 'react';
import { Link } from 'react-router-dom';
import { WordReveal } from '@/components/site/WordReveal';
import { Reveal } from '@/components/site/Reveal';
import { SectionLabel } from '@/components/site/SectionLabel';
import { MagneticButton } from '@/components/site/MagneticButton';
import { HeroImage } from '@/components/site/HeroImage';
import { FAQList } from '@/components/site/FAQ';
import { CTABand } from '@/components/site/CTABand';
import { FaqSchema } from '@/components/site/Seo';
import { WRAP, SECTION, H2, H2_SM } from '@/components/site/ServicePageParts';
import { SITE } from '@/data/site';
import { LANDINGS, OWN_PRODUCTS } from '@/data/landings';

const LINK_CLS = 'nd-link-u text-white';
const RULE = 'border-white/[0.08]';
const n2 = (i) => String(i + 1).padStart(2, '0');

const ArrowDisc = ({ className = '' }) => (
  <span aria-hidden className={`h-12 w-12 shrink-0 rounded-full border border-white/15 grid place-items-center transition-colors duration-300 group-hover:bg-white group-hover:text-ink-bg group-hover:border-white ${className}`}>
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.6" className="transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:-rotate-45"><path d="M2 8h12M9 3l5 5-5 5" /></svg>
  </span>
);

/** Two-column head: mono label left, display heading right. */
function Head({ num, label, heading, intro, small }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6 lg:gap-8 mb-14 md:mb-20">
      <SectionLabel num={num} className="lg:pt-4">{label}</SectionLabel>
      <div>
        <Reveal><h2 className={`${small ? H2_SM : H2} max-w-[22ch]`}>{heading}</h2></Reveal>
        {intro && (
          <Reveal delay={100}><p className="mt-7 text-ink-muted text-[17px] leading-relaxed max-w-[60ch]">{intro}</p></Reveal>
        )}
      </div>
    </div>
  );
}

function Hero({ page }) {
  return (
    <section className="relative overflow-hidden">
      <div aria-hidden className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[12vw] -top-[18vw] h-[70vw] w-[70vw] max-h-[1000px] max-w-[1000px] rounded-full blur-[20px]"
        style={{ background: 'radial-gradient(circle at 40% 45%, rgba(108,92,231,.32), rgba(75,159,255,.12) 40%, transparent 68%)' }}
      />
      <div className={`relative ${WRAP} pt-16 md:pt-24 pb-20 md:pb-28`}>
        <nav aria-label="Breadcrumb" className="nd-mono flex flex-wrap items-center gap-2.5 !text-[11px]">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span aria-hidden>/</span>
          {page.crumbParent && (
            <>
              <Link to={page.crumbParent.to} className="hover:text-white transition-colors">{page.crumbParent.label}</Link>
              <span aria-hidden>/</span>
            </>
          )}
          <span className="text-white">{page.crumbLabel}</span>
        </nav>

        <SectionLabel as="h1" num="—" data-testid={`landing-${page.slug}-h1`} className="mt-12 md:mt-16">
          {page.h1}
        </SectionLabel>
        <WordReveal
          as="p"
          text={page.headline}
          accent={page.accent}
          className="mt-8 max-w-[15ch] font-display font-semibold text-[clamp(44px,7.4vw,128px)] leading-[0.92] tracking-[-0.05em]"
        />

        <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-5">
            <HeroImage src={page.image.src} alt={page.image.alt} testId={`landing-${page.slug}-hero-image`} />
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <Reveal delay={350}>
              <p className="text-lg md:text-xl text-[#C9C9D3] leading-relaxed max-w-[46ch]">{page.intro}</p>
            </Reveal>
            <Reveal delay={450}>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <MagneticButton href={SITE.whatsapp} external variant="primary">Get a fixed quote</MagneticButton>
                <MagneticButton to="/contact/" variant="ghost">Contact us</MagneticButton>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

function Story({ story, num }) {
  const [first, ...rest] = story.paras;
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num={num}>{story.label}</SectionLabel>
            <Reveal><h2 className={`mt-7 ${H2_SM}`}>{story.heading}</h2></Reveal>
          </div>
        </div>
        <div className="lg:col-span-7 space-y-7">
          <Reveal>
            <p className="font-display font-medium text-[clamp(22px,2.2vw,32px)] leading-[1.25] tracking-[-0.025em] text-white">{first}</p>
          </Reveal>
          {rest.map((para, i) => (
            <Reveal key={i} delay={(i + 1) * 80}>
              <p className="text-lg text-ink-muted leading-relaxed max-w-[62ch]">{para}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Cards({ cards, num }) {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <Head num={num} label={cards.label} heading={cards.heading} />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/[0.08] rounded-[18px] overflow-hidden ring-1 ring-white/[0.08]">
          {cards.items.map((c, i) => (
            <Reveal key={c.k} delay={(i % 3) * 70} className="bg-ink-bg">
              <div className="h-full min-h-[240px] p-8 md:p-10 flex flex-col">
                <span className="nd-mono !text-[11px]">{n2(i)}</span>
                <h3 className="mt-auto pt-14 font-display text-[24px] md:text-[28px] font-semibold tracking-[-0.035em] leading-[1.05]">{c.k}</h3>
                <p className="mt-4 text-ink-muted leading-relaxed text-[15px] max-w-md">{c.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function KeyList({ list, num }) {
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num={num}>{list.label}</SectionLabel>
            <Reveal><h2 className={`mt-7 ${H2_SM}`}>{list.heading}</h2></Reveal>
          </div>
        </div>
        <ol className={`lg:col-span-8 border-t ${RULE}`}>
          {list.items.map((w, i) => (
            <Reveal as="li" key={w.k} delay={i * 50} className={`border-b ${RULE}`}>
              <div className="grid grid-cols-[48px_1fr] md:grid-cols-[72px_minmax(0,0.9fr)_minmax(0,1.3fr)] gap-x-6 gap-y-3 py-8">
                <span className="nd-mono pt-1.5">{n2(i)}</span>
                <h3 className="font-display text-xl md:text-2xl font-semibold tracking-[-0.03em] leading-tight">{w.k}</h3>
                <p className="col-start-2 md:col-start-3 text-ink-muted leading-relaxed">{w.v}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

function DataTable({ table, num }) {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <Head num={num} label={table.label} heading={table.heading} intro={table.intro} />
        <Reveal>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-[15px] border-t border-white/[0.14]">
              <thead>
                <tr className={`border-b ${RULE}`}>
                  {table.cols.map((c, i) => (
                    <th key={i} scope="col" className="nd-mono !text-[11px] text-left font-normal py-5 pr-6">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => {
                  const ours = String(row[0]).includes('NioDelta');
                  return (
                    <tr key={row[0]} className={`border-b ${RULE} ${ours ? 'bg-violet/[0.07]' : ''}`}>
                      <th scope="row" className={`py-6 pr-6 text-left align-top font-display text-lg font-semibold tracking-[-0.02em] ${ours ? 'pl-4 border-l-2 border-violet' : ''}`}>{row[0]}</th>
                      {row.slice(1).map((cell, i) => (
                        <td key={i} className={`py-6 pr-6 align-top tabular-nums ${ours ? 'text-white' : 'text-ink-muted'}`}>{cell}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Reveal>
        {table.note && <p className="mt-6 text-xs text-ink-muted max-w-[90ch] leading-relaxed">{table.note}</p>}
      </div>
    </section>
  );
}

function ChipColumns({ chips, num }) {
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-16`}>
        {chips.map((col) => (
          <div key={col.heading}>
            <SectionLabel num={num}>Coverage</SectionLabel>
            <Reveal><h2 className="mt-7 font-display font-semibold text-[clamp(26px,2.6vw,40px)] leading-[1.05] tracking-[-0.035em] max-w-[20ch]">{col.heading}</h2></Reveal>
            <ul className={`mt-8 border-t ${RULE}`}>
              {col.items.map((x, i) => (
                <li key={x} className={`flex items-baseline gap-5 border-b ${RULE} py-3.5`}>
                  <span className="nd-mono !text-[11px] w-8 shrink-0">{n2(i)}</span>
                  <span className="text-[#C9C9D3]">{x}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Only products the team builds and runs itself: verifiable, no client claims. */
export function OwnProducts({ num }) {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <Head
          num={num}
          label="Live products"
          heading="Apps we build and run ourselves"
          intro="We do not only build for clients. Our team ships and supports its own products, so we deal with store reviews, OS updates and real users every week."
        />
        <ul className={`border-t ${RULE}`}>
          {OWN_PRODUCTS.map((p, i) => (
            <Reveal as="li" key={p.name} delay={i * 60} className={`border-b ${RULE}`}>
              <Link
                to={p.caseStudy}
                className="group relative isolate grid grid-cols-[1fr_auto] lg:grid-cols-[220px_1fr_auto] gap-x-8 gap-y-3 items-center py-8 md:py-10"
              >
                <span aria-hidden className="absolute inset-y-0 -inset-x-4 md:-inset-x-12 -z-10 origin-bottom scale-y-0 bg-[linear-gradient(90deg,rgba(108,92,231,.14),rgba(75,159,255,.04))] transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:scale-y-100" />
                <span className="nd-mono col-span-2 lg:col-span-1">{p.kind}</span>
                <h3 className="font-display font-semibold text-[clamp(32px,4.4vw,68px)] tracking-[-0.045em] leading-none transition-transform duration-500 [transition-timing-function:var(--ease-out)] group-hover:translate-x-4">
                  {p.name}
                </h3>
                <ArrowDisc className="md:h-14 md:w-14" />
                <div className="col-span-2 lg:col-start-2 lg:col-span-1">
                  <p className="text-ink-muted text-[15px] leading-relaxed max-w-[60ch]">{p.body}</p>
                  <span className="mt-4 inline-block text-sm nd-grad-text">Read the case study →</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Splits "10–12 weeks · scope" into a mono timeline and the scope line. */
function splitNote(note = '') {
  const idx = note.indexOf(' · ');
  return idx > -1 ? [note.slice(0, idx), note.slice(idx + 3)] : [null, note];
}

function Cost({ cost, num }) {
  return (
    <section className={SECTION}>
      <div className={WRAP}>
        <Head num={num} label="Cost & timelines" heading={cost.heading} intro={cost.intro} />
        <div className={`grid grid-cols-1 md:grid-cols-3 border-t ${RULE}`}>
          {cost.tiers.map((t, i) => {
            const [when, scope] = splitNote(t.note);
            return (
              <Reveal key={t.name} delay={i * 80} className={`border-b md:border-b-0 ${RULE} ${i ? 'md:border-l md:pl-8' : ''} md:pr-8`}>
                <div className="h-full flex flex-col pt-8 pb-10">
                  <div className="flex items-center justify-between">
                    <span className="nd-mono !text-[11px]">{n2(i)} / {String(cost.tiers.length).padStart(2, '0')}</span>
                    {when && <span className="nd-mono !text-[11px] text-white">{when}</span>}
                  </div>
                  <h3 className="mt-10 font-display text-2xl font-semibold tracking-[-0.03em]">{t.name}</h3>
                  <div className="mt-4 font-display font-semibold text-[clamp(30px,2.8vw,44px)] leading-[1.02] tracking-[-0.045em] tabular-nums text-balance">{t.from}</div>
                  <p className="mt-6 text-[15px] text-ink-muted leading-relaxed max-w-[38ch]">{scope}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <p className="mt-8 text-sm text-ink-muted">
          Prices exclude GST.
          {cost.link && (
            <>
              {' '}
              <Link to={cost.link.to} className={LINK_CLS}>{cost.link.label}</Link>.
            </>
          )}
        </p>
      </div>
    </section>
  );
}

function Faq({ page, num }) {
  return (
    <section className={SECTION}>
      <div className={`${WRAP} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32">
            <SectionLabel num={num}>FAQ</SectionLabel>
            <Reveal><h2 className={`mt-7 ${H2_SM}`}>{page.faqHeading}</h2></Reveal>
            <div className="mt-10 nd-mono !text-[11px]">Related pages</div>
            <ul className={`mt-4 border-t ${RULE}`}>
              {page.links.map((l) => (
                <li key={l.to} className={`border-b ${RULE}`}>
                  <Link to={l.to} className="arrow-link group flex items-center justify-between gap-4 py-3.5 text-[15px] text-[#C9C9D3] hover:text-white transition-colors">
                    {l.label} <span className="arrow text-ink-muted group-hover:text-white">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="lg:col-span-8">
          <FAQList items={page.faqs} testIdPrefix={`landing-${page.slug}-faq`} />
        </div>
      </div>
    </section>
  );
}

export default function Landing({ slug }) {
  const page = LANDINGS.find((l) => l.slug === slug);
  // Section order: the cost page leads with prices, the rest with context.
  const blocks = [
    page.cost?.first && ['cost', page.cost],
    page.story && ['story', page.story],
    page.cards && !page.cost?.first && ['cards', page.cards],
    page.table && ['table', page.table],
    page.list && ['list', page.list],
    page.cards && page.cost?.first && ['cards', page.cards],
    page.chips && ['chips', page.chips],
    page.proof && ['proof', true],
    page.cost && !page.cost.first && ['cost', page.cost],
  ].filter(Boolean);

  return (
    <div data-testid={`landing-${slug}-page`}>
      <FaqSchema items={page.faqs} />
      <Hero page={page} />
      {blocks.map(([kind, data], i) => {
        const n = n2(i);
        if (kind === 'story') return <Story key={kind} story={data} num={n} />;
        if (kind === 'cards') return <Cards key={kind} cards={data} num={n} />;
        if (kind === 'table') return <DataTable key={kind} table={data} num={n} />;
        if (kind === 'list') return <KeyList key={kind} list={data} num={n} />;
        if (kind === 'chips') return <ChipColumns key={kind} chips={data} num={n} />;
        if (kind === 'proof') return <OwnProducts key={kind} num={n} />;
        return <Cost key={kind} cost={data} num={n} />;
      })}
      <Faq page={page} num={n2(blocks.length)} />
      <CTABand eyebrow={page.cta.eyebrow} headline={page.cta.headline} sub={page.cta.sub} />
    </div>
  );
}
